/**
 * SSRF egress policy [G-9] (ARCHITECTURE §13) — the single source of truth
 * for the deny-by-default private/link-local/loopback range check shared by
 * every service that opens a connection to a user-configured upstream host
 * (runner egress-proxy, the git-repo source connector, …).
 *
 * Deny-by-default for private / link-local / loopback ranges, with the
 * operator knob (`EGRESS_ALLOW_PRIVATE_CIDRS` for the runner,
 * `GIT_EGRESS_ALLOW_PRIVATE_CIDRS` for the git connector — same semantics,
 * comma-separated CIDRs) to admit LAN Ollama/SearXNG/GitLab deployments
 * without disabling the default.
 *
 * Kept pure (no service/DB/env dependency) so it can be unit-tested directly
 * and re-exported verbatim by `@llm-wiki/runner`'s net-policy — DO NOT fork
 * the CIDR ranges anywhere; import them from here.
 */
import { isIP } from "node:net";
import { lookup } from "node:dns/promises";
function ipv4ToInt(ip) {
    const parts = ip.split(".");
    if (parts.length !== 4)
        return null;
    let out = 0;
    for (const p of parts) {
        const n = Number(p);
        if (!Number.isInteger(n) || n < 0 || n > 255)
            return null;
        out = out * 256 + n;
    }
    return out >>> 0;
}
export function parseCidr(cidr) {
    const [ip, bitsRaw] = cidr.split("/");
    if (!ip)
        return null;
    const base = ipv4ToInt(ip);
    if (base === null)
        return null;
    const bits = bitsRaw === undefined ? 32 : Number(bitsRaw);
    if (!Number.isInteger(bits) || bits < 0 || bits > 32)
        return null;
    return { base, maskBits: bits };
}
function inCidr4(ipInt, cidr) {
    if (cidr.maskBits === 0)
        return true;
    const mask = cidr.maskBits === 32 ? 0xffffffff : (~((1 << (32 - cidr.maskBits)) - 1) >>> 0);
    return ((ipInt & mask) >>> 0) === ((cidr.base & mask) >>> 0);
}
const PRIVATE_V4 = [
    "0.0.0.0/8",
    "10.0.0.0/8",
    "100.64.0.0/10",
    "127.0.0.0/8",
    "169.254.0.0/16",
    "172.16.0.0/12",
    "192.168.0.0/16",
    "192.0.0.0/24",
    "198.18.0.0/15",
].map((c) => parseCidr(c));
function isPrivateV6(ip) {
    const lower = ip.toLowerCase();
    return (lower === "::" ||
        lower === "::1" ||
        lower.startsWith("fe80") || // link-local
        lower.startsWith("fc") || // ULA fc00::/7
        lower.startsWith("fd") ||
        lower.startsWith("::ffff:") // v4-mapped — re-checked as v4 below
    );
}
export function isPrivateAddress(ip) {
    const family = isIP(ip);
    if (family === 4) {
        const n = ipv4ToInt(ip);
        if (n === null)
            return true; // unparsable → fail closed
        return PRIVATE_V4.some((c) => inCidr4(n, c));
    }
    if (family === 6) {
        if (ip.toLowerCase().startsWith("::ffff:")) {
            const v4 = ip.slice(ip.lastIndexOf(":") + 1);
            if (isIP(v4) === 4)
                return isPrivateAddress(v4);
        }
        return isPrivateV6(ip);
    }
    return true; // not an IP → fail closed (caller resolves first)
}
export function isAllowedByCidrList(ip, allowCidrs) {
    if (isIP(ip) !== 4)
        return false; // the operator knob is v4 CIDRs
    const n = ipv4ToInt(ip);
    if (n === null)
        return false;
    return allowCidrs.some((raw) => {
        const cidr = parseCidr(raw.trim());
        return cidr !== null && inCidr4(n, cidr);
    });
}
const systemResolver = async (host) => (await lookup(host, { all: true, verbatim: true })).map(({ address, family }) => ({
    address,
    family,
}));
/**
 * Resolve `host` and enforce the SSRF policy: every resolved address must
 * be public, OR covered by the operator's allow-CIDR list. Throws on
 * violation (fail closed, including DNS errors).
 *
 * Returns the vetted addresses so the caller can PIN one and connect to it
 * directly for the session lifetime — re-resolving per request would let a
 * host that passed this gate rebind to a private address later (DNS
 * rebinding, [F3]).
 */
export async function assertEgressAllowed(host, allowPrivateCidrs, resolveHost = systemResolver) {
    const family = isIP(host);
    const addresses = family !== 0 ? [{ address: host, family }] : await resolveHost(host);
    if (addresses.length === 0) {
        throw new Error(`egress denied: ${host} did not resolve`);
    }
    for (const { address } of addresses) {
        if (isPrivateAddress(address) && !isAllowedByCidrList(address, allowPrivateCidrs)) {
            throw new Error(`egress denied: ${host} resolves to private address ${address} (set an egress allow-CIDR knob to permit it, [G-9])`);
        }
    }
    return addresses;
}
//# sourceMappingURL=net-policy.js.map